/**
 * Libellés des modules (D21) : ceux du registre partagé
 * (`commun:modules.<id>.libelle` de `@artosera/shared`), pas une copie propre
 * à la console qui finirait par diverger.
 */
import { useTranslation } from 'react-i18next';
import { libelle, type Langue } from '@artosera/shared';
import { ApiError } from './api.js';

export function langueCourante(langue: string | undefined): Langue {
  return langue?.startsWith('en') ? 'en' : 'fr';
}

export interface LibellesModules {
  langue: Langue;
  libelle: (module: string) => string;
  description: (module: string) => string;
  /** « Offres et Boutique », « Offers and Shop ». */
  liste: (modules: readonly string[]) => string;
}

export function useLibellesModules(): LibellesModules {
  const { i18n } = useTranslation('console');
  const langue = langueCourante(i18n.language);
  const libelleModule = (module: string): string =>
    libelle('commun', `modules.${module}.libelle`, langue);
  return {
    langue,
    libelle: libelleModule,
    description: (module) => libelle('commun', `modules.${module}.description`, langue),
    liste: (modules) =>
      new Intl.ListFormat(langue, { style: 'long', type: 'conjunction' }).format(
        modules.map(libelleModule),
      ),
  };
}

/** La raison machine d'une erreur de l'API (`details.raison`), si elle en a une. */
export function raisonErreur(erreur: unknown): string | null {
  if (!(erreur instanceof ApiError)) return null;
  const details = erreur.details as { raison?: unknown } | null;
  return typeof details?.raison === 'string' ? details.raison : null;
}

/** Les modules requis que l'API signale manquants (`details.dependances`). */
export function dependancesErreur(erreur: unknown): string[] {
  if (!(erreur instanceof ApiError)) return [];
  const details = erreur.details as { dependances?: unknown } | null;
  return Array.isArray(details?.dependances)
    ? details.dependances.filter((valeur): valeur is string => typeof valeur === 'string')
    : [];
}
